export const GOOGLE_FONT_FAMILY = /^[A-Za-z0-9][A-Za-z0-9 ]{0,59}$/;

export const THEME_FONT_GENERICS = [
  'serif',
  'sans-serif',
  'cursive',
  'monospace',
  'fantasy',
];

export const MAX_THEME_FONTS = 3;

export const MAX_DOCUMENT_FONTS = 4;

export const GOOGLE_CATEGORY_GENERIC: Record<string, string> = {
  Serif: 'serif',
  'Sans Serif': 'sans-serif',
  Monospace: 'monospace',
  Handwriting: 'cursive',
  Display: 'sans-serif',
};

export const normalizeFontValue = (value: string): string =>
  value
    .replace(/\s*!important\s*$/i, '')
    .replace(/'/g, '"')
    .replace(/\s*,\s*/g, ', ')
    .replace(/\s+/g, ' ')
    .trim();

type GoogleFont = { family: string; generic: string };

const parseGoogleFont = (value: string): GoogleFont | null => {
  const match = /^"([^"]*)", ([a-z-]+)$/.exec(normalizeFontValue(value));
  if (
    match == null ||
    !GOOGLE_FONT_FAMILY.test(match[1]!) ||
    !THEME_FONT_GENERICS.includes(match[2]!)
  ) {
    return null;
  }
  return { family: match[1]!, generic: match[2]! };
};

export const googleFontFromValue = (value: string): string | null =>
  parseGoogleFont(value)?.family ?? null;

export const googleFontValue = (family: string, generic: string): string =>
  `"${family}", ${generic}`;

export const stripCssBreakout = (value: string): string =>
  value.replace(/[<>{};]/g, '');

export const stripCssBreakoutOrNull = (
  value: string | null | undefined,
): string | null => (value == null ? null : stripCssBreakout(value));

export const themeFontFamilies = (
  themeContent: string | null | undefined,
): string[] => {
  const families = new Set<string>();
  for (const match of (themeContent ?? '').matchAll(
    /--theme-font\s*:\s*([^;}<]*)/gi,
  )) {
    const family = googleFontFromValue(match[1]!);
    if (family != null) {
      families.add(family);
    }
  }
  return [...families].slice(0, MAX_THEME_FONTS);
};

const reportGoogleFont = (
  reportFont: string | null | undefined,
): GoogleFont | null =>
  typeof reportFont === 'string' ? parseGoogleFont(reportFont) : null;

export const documentFontFamilies = (
  themeContent: string | null | undefined,
  reportFont: string | null | undefined,
): string[] => {
  const families = themeFontFamilies(themeContent);
  const reportFamily = reportGoogleFont(reportFont)?.family;
  if (reportFamily != null && !families.includes(reportFamily)) {
    families.push(reportFamily);
  }
  return families.slice(0, MAX_DOCUMENT_FONTS);
};

export const reportFontOverrideCss = (
  reportFont: string | null | undefined,
): string | null => {
  const font = reportGoogleFont(reportFont);
  if (font == null) {
    return null;
  }
  const value = googleFontValue(font.family, font.generic);
  return `:root,body{--theme-font:${value} !important}`;
};

export const injectIntoHead = (html: string, markup: string): string => {
  const headEnd = html.search(/<\/head\s*>/i);
  if (headEnd === -1) {
    return html + markup;
  }
  return html.slice(0, headEnd) + markup + html.slice(headEnd);
};
